import React, { useMemo } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import {
  NAV_LINKS,
  ANALYTICS_NAV_LINKS,
  ADMIN_LINK_IDS_BY_ROLE,
  ROLE_NAV_PRIORITY,
  isNavItemAllowed,
  isNavItemRoleAllowed
} from './navConfig';

const Sidebar = ({
  user,
  roles = [],
  onSelect,
  active,
  enabledFeatures = [],
  isEntitlementsLoaded = true,
  // Backwards-compatible legacy props:
  hasSurveyInsightsFeature,
  hasAdvancedAnalyticsFeature,
  hasHeatmapFeature,
  hasCustomerAnalyticsFeature,
  hasExtensionPlayerFeature,
  isOpen,
  isCollapsed = false,
  onToggleCollapse,
  onClose
}) => {
  const normalizedRoles = roles.map(r => String(r || '').toLowerCase());
  const matchedRole = ROLE_NAV_PRIORITY.find((role) => normalizedRoles.includes(role)) || 'default';
  const isSystemAdmin = normalizedRoles.includes('systemadministrator');

  // Compute effective enabled features, allowing legacy boolean props to override if explicitly passed
  const effectiveEnabledFeatures = useMemo(() => {
    const set = new Set(enabledFeatures || []);
    if (hasSurveyInsightsFeature === true) { set.add('survey_module'); set.add('survey_insights'); }
    if (hasAdvancedAnalyticsFeature === true) { set.add('advanced_analytics'); }
    if (hasHeatmapFeature === true) { set.add('heat_map'); }
    if (hasCustomerAnalyticsFeature === true) { set.add('customer_analytics'); }
    if (hasExtensionPlayerFeature === true) { set.add('extension_player'); }

    if (hasHeatmapFeature === false) { set.delete('heat_map'); }
    if (hasCustomerAnalyticsFeature === false) { set.delete('customer_analytics'); }
    if (hasExtensionPlayerFeature === false) { set.delete('extension_player'); }

    return Array.from(set);
  }, [
    enabledFeatures,
    hasSurveyInsightsFeature,
    hasAdvancedAnalyticsFeature,
    hasHeatmapFeature,
    hasCustomerAnalyticsFeature,
    hasExtensionPlayerFeature
  ]);

  const userContext = {
    roles,
    enabledFeatures: effectiveEnabledFeatures,
    isEntitlementsLoaded,
    isSystemAdmin
  };

  const visibleLinks = (ADMIN_LINK_IDS_BY_ROLE[matchedRole] || ADMIN_LINK_IDS_BY_ROLE.default)
    .map(linkId => NAV_LINKS[linkId])
    .filter(Boolean)
    .filter(item => isNavItemAllowed(item, userContext));

  const visibleAnalyticsLinks = ANALYTICS_NAV_LINKS
    .filter(item => isNavItemRoleAllowed(item, userContext));

  return (
    <aside
      className={`sidebar ${isOpen ? 'sidebar--open' : ''} ${isCollapsed ? 'sidebar--collapsed' : ''}`}
      aria-label="Main Navigation"
    >
      {/* Mobile Close Button */}
      {isOpen && (
        <div className="sidebar-mobile-header">
          <button
            type="button"
            className="sidebar-close-btn"
            onClick={onClose}
            aria-label="Close sidebar"
          >
            ✕
          </button>
        </div>
      )}

      <div className="sidebar-sections">
        <div className="sidebar-section">
          {!isCollapsed && <div className="sidebar-label">Admin Dashboard</div>}
          <ul className="sidebar-links">
            {visibleLinks.map(link => (
              <li key={link.id}>
                <button
                  type="button"
                  className={`sidebar-button ${active === link.id ? 'active' : ''}`}
                  onClick={() => onSelect(link.id)}
                  aria-current={active === link.id ? 'page' : undefined}
                  data-tooltip={link.label}
                  title={isCollapsed ? link.label : undefined}
                >
                  <span className="icon-wrapper">{link.icon}</span>
                  <span className="label-text">{link.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {visibleAnalyticsLinks.length > 0 && (
          <div className="sidebar-section">
            {!isCollapsed ? (
              <div className="sidebar-label">Analytics</div>
            ) : (
              <div className="sidebar-divider" title="Analytics" />
            )}
            <ul className="sidebar-links">
              {visibleAnalyticsLinks.map(link => {
                const isActive = active === link.id || (link.aliases && link.aliases.includes(active));

                return (
                  <li key={link.id}>
                    <button
                      type="button"
                      className={`sidebar-button ${isActive ? 'active' : ''}`}
                      onClick={() => onSelect(link.id)}
                      aria-current={isActive ? 'page' : undefined}
                      data-tooltip={link.label}
                      title={isCollapsed ? link.label : undefined}
                    >
                      <span className="icon-wrapper">{link.icon}</span>
                      <span className="label-text">{link.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {onToggleCollapse && (
        <button
          type="button"
          className="sidebar-edge-toggle"
          onClick={onToggleCollapse}
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? <FaChevronRight size={10} /> : <FaChevronLeft size={10} />}
        </button>
      )}
    </aside>
  );
};

export default Sidebar;